/** @file This file exports a client for the v2 version of our API. */
import { FetchHttpHandler } from '@smithy/fetch-http-handler';
import { Apiv2Client } from '@codegrade/apiv2-client';
import router from './router';
import * as storeMod from './store';
import { signal } from './utils';
/**
 * Perform a `notLoggedInError` action.
 *
 * This method might navigate the user away.
 *
 * @param message The message displayed to the user.
 */
function doNotLoggedInError(message) {
    // @ts-expect-error This isn't defined, but should exist.
    // eslint-disable-next-line
    const app = window.__app__;
    if (app != null) {
        app.notLoggedInError(message);
        if (router.currentRoute.name !== 'login') {
            app.$router.logoutAndRedirectAfterLogin();
        }
    }
}
/** A custom implementation of the fetch handler. */
class Handler extends FetchHttpHandler {
    /**
     * Handle a request with fetch.
     *
     * @param request The request to handle.
     * @param opts Optional options on how to handle the request.
     * @returns Whatever `FetchHttpHandle` returns.
     */
    async handle(request, opts) {
        const resp = await super.handle(request, opts);
        if (resp.response.statusCode === 401 && request.method.toUpperCase() === 'GET') {
            const token = storeMod.UserStore.dangerousSession().extract();
            if (token == null) {
                doNotLoggedInError('You are currently not logged in. Please log in to view this page.');
            }
            else if (token.token === request.headers.Authorization?.replace(/^Bearer\s*/, '')) {
                doNotLoggedInError('Your session has expired. Please log in again to view this page.');
            }
        }
        return resp;
    }
}
const requestHandler = new Handler({ keepAlive: true });
let innerClient;
// Do not replace this with using the option for `token` to be a method on the `Apiv2Client`. That
// method is cached for an unspecified amount of time, which causes login/logouts or leave/enter
// student view to use the wrong token.
signal.STORE_CREATED.register(async (store) => {
    store.watch(state => state.user.loggedInUser.extract()?.session.token ?? '', token => {
        innerClient = new Apiv2Client({
            region: 'eu-west-1',
            endpoint: `${window.location.origin}/api/v2`,
            token: { token },
            requestHandler,
            cacheMiddleware: true,
        });
    }, { immediate: true });
});
/**
 * A client for the v2 version of the API.
 *
 * It is safe to pass this value as a prop in Vue components.
 */
export const client = Object.freeze({
    // @ts-expect-error As `send` is overloaded Typescript doesn't understand this magic.
    send: (...args) => innerClient.send(...args),
});
